import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminPostApi, AdminPost, AdminPostListParams } from '../../../services/admin/adminPostApi';
import { getPostCategories, PostCategory } from '../../../services/admin/postCategoryApi';
import {
  Container,
  MainContent,
  TitleSection,
  PageTitle,
  FilterSection,
  FilterControls,
  Dropdown,
  DropdownSelect,
  DropdownButton,
  DropdownIcon,
  TableContainer,
  TableInner,
  TableHeaderWrapper,
  TableHeaderBackground,
  TableHeaderBg,
  TableHeader,
  HeaderCell,
  HeaderCellContent,
  HeaderText,
  TableBody,
  TableRow,
  TableCell,
  TableCellContent,
  CellText,
  TitleText,
  ActionButtons,
  DeleteButton,
  EditButton,
  HideButton,
  NoData,
  Loading,
  ErrorContainer,
  RetryButton,
  ModalOverlay,
  DateModal,
  DeleteModal,
  ModalHeader,
  ModalActions,
  CancelButton,
  ConfirmButton,
  DeleteIcon,
  DeleteModalTitle,
  DeleteWarning,
  DeleteForm,
  Optional,
  ReasonTextarea,
  DeleteActions,
} from './AdminPostManagementStyles';
import { AdminDateFilterModal } from '../common';
import AdminSearchInput from '../common/AdminSearchInput';
import CommunityTablePagination from './CommunityTablePagination';

const AdminPostManagementComponent: React.FC = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<AdminPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<PostCategory[]>([]);

  // 필터 상태
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null | undefined>(null);
  const [selectedStatus, setSelectedStatus] = useState(''); // 기본값을 빈 문자열로 변경
  const [searchQuery, setSearchQuery] = useState('');

  // 날짜 필터 상태 (적용된 날짜)
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');

  // 날짜 모달 상태 (임시 선택 날짜)
  const [showDateModal, setShowDateModal] = useState(false);
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');

  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // 모달 상태
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePostId, setDeletePostId] = useState<number | null>(null);

  // 초기 로딩 여부 추적 (첫 번째 fetch 완료 전까지만 true)
  const isFirstLoad = useRef(true);
  const isInitialLoading = loading && isFirstLoad.current;

  // 날짜 형식 검증 함수
  const isValidDateFormat = useCallback((dateString: string): boolean => {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateString)) return false;

    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime()) && dateString === date.toISOString().split('T')[0];
  }, []);

  // 날짜 범위 유효성 검증 함수
  const validateDateRange = useCallback((fromDate: string, toDate: string): boolean => {
    try {
      // 둘 다 비어있으면 유효
      if (!fromDate && !toDate) return true;

      // 하나만 있으면 유효
      if (!fromDate || !toDate) return true;

      // 둘 다 유효한 날짜 형식인지 확인
      if (!isValidDateFormat(fromDate) || !isValidDateFormat(toDate)) {
        /// console.error('유효하지 않은 날짜 형식:', { fromDate, toDate });
        return false;
      }

      const fromDateObj = new Date(fromDate);
      const toDateObj = new Date(toDate);

      // from이 to보다 뒤에 있는지 확인
      if (fromDateObj > toDateObj) {
        /// console.error('시작 날짜가 종료 날짜보다 뒤에 있습니다:', { fromDate, toDate });
        return false;
      }

      return true;
    } catch (error) {
      /// console.error('날짜 범위 검증 중 오류 발생:', error, { fromDate, toDate });
      return false;
    }
  }, [isValidDateFormat]);

  // 카테고리 목록 조회
  const fetchCategories = useCallback(async () => {
    try {
      const response = await getPostCategories();
      setCategories(response.data);

      // 기본 카테고리 선택 제거 - 사용자가 직접 선택하도록 함
    } catch (err) {
      /// console.error('카테고리 목록 조회 실패:', err);
    }
  }, []);

  // 게시글 목록 조회
  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // 날짜 범위 유효성 검증
      if (!validateDateRange(appliedStartDate, appliedEndDate)) {
        /// console.error('유효하지 않은 날짜 범위로 인해 날짜 필터를 초기화합니다:', { fromDate: appliedStartDate, toDate: appliedEndDate });
        alert('유효하지 않은 날짜 범위입니다. 전체 기간으로 초기화됩니다.');
        // 날짜 필터 초기화
        setAppliedStartDate('');
        setAppliedEndDate('');
        setTempStartDate('');
        setTempEndDate('');

        // 초기화된 상태로 다시 호출
        const params: AdminPostListParams = {
          page: currentPage - 1,
          size: 10,
          postCategoryId: selectedCategoryId || null,
          from: undefined,
          to: undefined,
          status: selectedStatus === 'true' ? 'ACTIVE' : selectedStatus === 'false' ? 'HIDDEN' : undefined,
          search: searchQuery || undefined
        };

        const response = await adminPostApi.getAdminPosts(params);

        setPosts(response.content);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
        return;
      }

      const params: AdminPostListParams = {
        page: currentPage - 1, // 0-based indexing
        size: 10,
        postCategoryId: selectedCategoryId || null, // undefined 대신 null로 명시적 설정
        from: appliedStartDate || undefined,
        to: appliedEndDate || undefined,
        status: selectedStatus === 'true' ? 'ACTIVE' : selectedStatus === 'false' ? 'HIDDEN' : undefined,
        search: searchQuery || undefined
      };

      const response = await adminPostApi.getAdminPosts(params);

      setPosts(response.content);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
    } catch (err) {
      setError('게시글 목록을 불러오는데 실패했습니다.');
      /// console.error('Error fetching posts:', err);
    } finally {
      isFirstLoad.current = false;
      setLoading(false);
    }
  }, [currentPage, selectedCategoryId, appliedStartDate, appliedEndDate, selectedStatus, searchQuery, validateDateRange]);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    fetchCategories();

    // 날짜 기본값을 전체 기간으로 설정 (빈 문자열로 설정)
    setAppliedStartDate('');
    setAppliedEndDate('');
    setTempStartDate('');
    setTempEndDate('');
  }, [fetchCategories]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);


  // 검색 실행
  const handleSearch = () => {
    setCurrentPage(1);
    fetchPosts();
  };

  // 엔터키 검색
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // 제목 클릭 시 커뮤니티 게시글로 이동
  const handleTitleClick = (postId: number) => {
    navigate(`/community/post/${postId}`);
  };

  // HTML 태그 제거 함수
  const stripHtmlTags = (html: string): string => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').trim();
  };

  // 삭제 처리
  const handleDeleteClick = (postId: number) => {
    setDeletePostId(postId);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async (reason?: string) => {
    if (deletePostId) {
      try {
        await adminPostApi.deletePost(deletePostId);
        setShowDeleteModal(false);
        setDeletePostId(null);
        await fetchPosts();
      } catch (err: any) {
        setError(err.message || '게시글 삭제에 실패했습니다.');
        /// console.error('Error deleting post:', err);
      }
    }
  };

  // 게시글 수정 (AdminWriteComponent로 이동)
  const handleEdit = (postId: number) => {
    navigate(`/admin/community/write?id=${postId}`);
  };

  // 숨김/표시 토글
  const handleHideToggle = async (postId: number) => {
    try {
      // 현재 게시글의 상태를 찾아서 반대로 설정
      const post = posts.find(p => p.id === postId);
      if (post) {
        const newVisible = post.status !== 'ACTIVE'; // ACTIVE가 아니면 true로, ACTIVE면 false로
        await adminPostApi.togglePostVisibility(postId, newVisible);
        await fetchPosts();
      }
    } catch (err: any) {
      setError(err.message || '게시글 상태 변경에 실패했습니다.');
      /// console.error('Error toggling post visibility:', err);
    }
  };


  // 페이지 이동
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // 페이지 직접 이동

  // 페이지네이션 렌더링

  if (isInitialLoading) {
    return (
      <Container>
        <MainContent>
          <TitleSection>
            <PageTitle>관리자 게시글 관리</PageTitle>
          </TitleSection>
          <Loading>로딩 중...</Loading>
        </MainContent>
      </Container>
    );
  }

  if (error && posts.length === 0) {
    return (
      <Container>
        <MainContent>
          <TitleSection>
            <PageTitle>관리자 게시글 관리</PageTitle>
          </TitleSection>
          <ErrorContainer>
            {error}
            <RetryButton onClick={fetchPosts}>
              다시 시도
            </RetryButton>
          </ErrorContainer>
        </MainContent>
      </Container>
    );
  }

  return (
    <>
      <Container>
        <MainContent>
          <TitleSection>
            <PageTitle>관리자 게시글 관리</PageTitle>
          </TitleSection>

          {/* 필터 및 검색 섹션 */}
          <FilterSection>
            <FilterControls>
              {/* 카테고리 드롭다운 */}
              <Dropdown>
                <DropdownSelect
                  value={selectedCategoryId || ''}
                  onChange={(e) => setSelectedCategoryId(e.target.value ? parseInt(e.target.value) : null)}
                >
                  <option value="">카테고리 선택</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </DropdownSelect>
                <DropdownIcon width="14" height="14" viewBox="0 0 14 14">
                  <path d="M4.17 5.25L7 8.08L9.83 5.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </DropdownIcon>
              </Dropdown>

              {/* 작성일 버튼 */}
              <Dropdown>
                <DropdownButton
                  onClick={() => {
                    setShowDateModal(true);
                    // 모달 열 때 현재 적용된 날짜를 임시 날짜로 설정
                    setTempStartDate(appliedStartDate);
                    setTempEndDate(appliedEndDate);
                  }}
                  style={{ cursor: 'pointer', textAlign: 'left' }}
                >
                  {appliedStartDate && appliedEndDate ? `작성일: ${appliedStartDate} ~ ${appliedEndDate}` :
                   appliedStartDate ? `작성일: ${appliedStartDate}` : '작성일'}
                </DropdownButton>
                <DropdownIcon width="14" height="14" viewBox="0 0 14 14">
                  <path d="M4.17 5.25L7 8.08L9.83 5.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </DropdownIcon>
              </Dropdown>

              {/* 상태 드롭다운 */}
              <Dropdown>
                <DropdownSelect
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  <option value="">상태 선택</option>
                  <option value="true">표시됨</option>
                  <option value="false">숨김</option>
                </DropdownSelect>
                <DropdownIcon width="14" height="14" viewBox="0 0 14 14">
                  <path d="M4.17 5.25L7 8.08L9.83 5.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </DropdownIcon>
              </Dropdown>
            </FilterControls>

            {/* 검색 입력 */}
            {/* <SearchBox>
              <div>
                <div>
                  <SearchIcon>
                    <img
                      src="/images/header/search.svg"
                      alt="검색"
                    />
                  </SearchIcon>
                </div>
                <div>
                  <SearchInput
                    type="text"
                    placeholder="검색어를 입력해주세요."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={handleKeyPress}
                  />
                </div>
              </div>
            </SearchBox> */}
            <AdminSearchInput
              value={searchQuery}
              onChange={(value) => {
                setSearchQuery(value);
                setCurrentPage(1);
              }}
              placeholder="게시글 제목을 입력해주세요."
            />
          </FilterSection>

          {/* 테이블 */}
          <TableContainer>
          <TableInner>
            {/* 테이블 헤더 */}
            <TableHeaderWrapper>
              <TableHeaderBackground>
                <TableHeaderBg></TableHeaderBg>
              </TableHeaderBackground>
              <TableHeader $columns="100px minmax(110px, 1fr) 100px 110px 180px">
                <HeaderCell variant="number">
                  <HeaderCellContent>
                    <HeaderText>연번</HeaderText>
                  </HeaderCellContent>
                </HeaderCell>
                <HeaderCell variant="title">
                  <HeaderCellContent>
                    <HeaderText>제목</HeaderText>
                  </HeaderCellContent>
                </HeaderCell>
                <HeaderCell variant="category">
                  <HeaderCellContent>
                    <HeaderText>카테고리</HeaderText>
                  </HeaderCellContent>
                </HeaderCell>
                <HeaderCell variant="date">
                  <HeaderCellContent>
                    <HeaderText>작성일</HeaderText>
                  </HeaderCellContent>
                </HeaderCell>
                <HeaderCell variant="actions">
                  <HeaderCellContent>
                    <HeaderText>관리</HeaderText>
                  </HeaderCellContent>
                </HeaderCell>
              </TableHeader>
            </TableHeaderWrapper>

            {/* 테이블 바디 */}
            <TableBody>
              {posts.length > 0 ? (
                posts.map((post, index) => (
                  <TableRow key={post.id} $columns="100px minmax(110px, 1fr) 100px 110px 180px">
                    <TableCell variant="number">
                      <TableCellContent>
                        <CellText>
                          {totalElements - (currentPage - 1) * 10 - index}
                        </CellText>
                      </TableCellContent>
                    </TableCell>
                    <TableCell variant="title">
                      <TableCellContent>
                        <TitleText
                          onClick={() => handleTitleClick(post.id)}
                          title={stripHtmlTags(post.title)}
                        >
                          {stripHtmlTags(post.title)}
                        </TitleText>
                      </TableCellContent>
                    </TableCell>
                    <TableCell variant="category">
                      <TableCellContent>
                        <CellText>{post.category}</CellText>
                      </TableCellContent>
                    </TableCell>
                    <TableCell variant="date">
                      <TableCellContent>
                        <CellText>
                          {new Date(post.createdAt).toLocaleDateString('ko-KR')}
                        </CellText>
                      </TableCellContent>
                    </TableCell>
                    <TableCell variant="actions">
                      <ActionButtons>
                        <DeleteButton
                          onClick={() => handleDeleteClick(post.id)}
                        >
                          삭제
                        </DeleteButton>
                        <EditButton
                          onClick={() => handleEdit(post.id)}
                        >
                          수정
                        </EditButton>
                        <HideButton
                          onClick={() => handleHideToggle(post.id)}
                        >
                          {post.status === 'ACTIVE' ? '숨기기' : '숨김 취소'}
                        </HideButton>
                      </ActionButtons>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <NoData>
                  등록된 게시글이 없습니다.
                </NoData>
              )}
            </TableBody>
          </TableInner>
          <CommunityTablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
          </TableContainer>
        </MainContent>

        {/* 삭제 확인 모달 */}
        {showDeleteModal && (
          <DeleteConfirmModal
            onConfirm={handleDeleteConfirm}
            onCancel={() => {
              setShowDeleteModal(false);
              setDeletePostId(null);
            }}
          />
        )}

        {/* 날짜 선택 모달 */}
        {showDateModal && (
          <AdminDateFilterModal
            title="작성일 선택"
            startDate={tempStartDate}
            endDate={tempEndDate}
            onStartDateChange={(date) => setTempStartDate(date)}
            onEndDateChange={(date) => setTempEndDate(date)}
            onConfirm={() => {
              // 적용 버튼을 눌렀을 때만 실제 날짜 필터를 적용
              setAppliedStartDate(tempStartDate);
              setAppliedEndDate(tempEndDate);
              setShowDateModal(false);
              setCurrentPage(1);
            }}
            onCancel={() => {
              // 취소 시 임시 날짜를 원래 적용된 날짜로 되돌림
              setTempStartDate(appliedStartDate);
              setTempEndDate(appliedEndDate);
              setShowDateModal(false);
            }}
          />
        )}
      </Container>
    </>
  );
};

// 삭제 확인 모달 컴포넌트
interface DeleteConfirmModalProps {
  onConfirm: (reason?: string) => void;
  onCancel: () => void;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  onConfirm,
  onCancel
}) => {
  const [reasonText, setReasonText] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm(reasonText);
    } catch (error) {
      /// console.error('Failed to delete post:', error);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <ModalOverlay onClick={onCancel}>
      <DeleteModal onClick={(e) => e.stopPropagation()}>
        <DeleteIcon>⚠️</DeleteIcon>
        <DeleteModalTitle>게시글을 삭제하시겠습니까?</DeleteModalTitle>
        <DeleteWarning>
          삭제된 게시글은 복구할 수 없습니다. 신중하게 결정해주세요.
        </DeleteWarning>

        <DeleteForm>
          <label>삭제 사유 <Optional>(선택사항)</Optional></label>
          <ReasonTextarea
            placeholder="삭제 사유를 입력해주세요"
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            rows={3}
          />
        </DeleteForm>

        <DeleteActions>
          <CancelButton
            onClick={onCancel}
            disabled={isConfirming}
          >
            취소
          </CancelButton>
          <ConfirmButton
            onClick={handleConfirm}
            disabled={isConfirming}
          >
            {isConfirming ? '삭제 중...' : '삭제'}
          </ConfirmButton>
        </DeleteActions>
      </DeleteModal>
    </ModalOverlay>
  );
};

export default AdminPostManagementComponent;
